import type { Organization, User } from './api';

const ALLOWED_REDIRECT_PREFIXES = ['/dashboard', '/admin', '/onboarding'] as const;

export function getSafeRedirectPath(path: string | null | undefined): string | null {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return null;
  const allowed = ALLOWED_REDIRECT_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
  return allowed ? path : null;
}

export function resolvePostAuthRedirect(
  user: User,
  organizations: Organization[],
  requestedPath?: string | null,
): string {
  const safeRequested = getSafeRedirectPath(requestedPath);

  if (!user.emailVerified || user.status === 'pending_verification') {
    return '/verify-email/pending';
  }

  if (user.isStaff) {
    if (safeRequested?.startsWith('/admin')) return safeRequested;
    if (safeRequested?.startsWith('/dashboard') && organizations.length > 0) {
      return safeRequested;
    }
    return '/admin';
  }

  if (organizations.length === 0) {
    return safeRequested?.startsWith('/onboarding') ? safeRequested : '/onboarding';
  }

  if (safeRequested?.startsWith('/dashboard')) return safeRequested;

  return '/dashboard';
}

export function canAccessAdmin(user: User | null | undefined): boolean {
  return !!user?.isStaff && !!user.emailVerified && user.status !== 'pending_verification';
}

export function canAccessDashboard(
  user: User | null | undefined,
  organizations: Organization[],
): boolean {
  if (!user?.emailVerified || user.status === 'pending_verification') return false;
  if (user.isStaff) return organizations.length > 0;
  return organizations.length > 0;
}

export function requiresOnboarding(
  user: User | null | undefined,
  organizations: Organization[],
): boolean {
  if (!user?.emailVerified || user.status === 'pending_verification') return false;
  if (user.isStaff) return false;
  return organizations.length === 0;
}
